import React, { useEffect, useState } from 'react';
import { WildfireEntity } from '../../../../types/wildfire';
import { fetchFireStats } from '../../../stats/statsService';
import type { FireStatsResponse, StatsChartData } from '../../../stats/statsTypes';
import StatsBarChart from '../../../stats/charts/StatsBarChart';
import StatsPieChart from '../../../stats/charts/StatsPieChart';

import './StatsTab.scss';

interface StatsTabProps {
    wildfire: WildfireEntity;
}

// Format hectares with comma separators and exactly one decimal (#565 — Scott).
function formatHa(value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    return `${value.toLocaleString(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    })} ha`;
}

// Format status for display
function formatStatus(status: string): string {
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Percentile descriptor
function percentileLabel(p: number): string {
    if (p >= 95) return 'exceptionally large';
    if (p >= 85) return 'very large';
    if (p >= 70) return 'larger than most';
    if (p >= 50) return 'above average';
    if (p >= 30) return 'below average';
    if (p >= 15) return 'smaller than most';
    return 'very small';
}

export const StatsTab: React.FC<StatsTabProps> = ({ wildfire }) => {
    const [stats, setStats] = useState<FireStatsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fireId = wildfire.fire_id;

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        fetchFireStats(fireId)
            .then(data => {
                if (!cancelled) {
                    setStats(data);
                    setLoading(false);
                }
            })
            .catch(err => {
                if (!cancelled) {
                    setError(err.message);
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [fireId]);

    if (loading) {
        return <div className="stats-tab__loading">Loading fire statistics...</div>;
    }

    if (error || !stats) {
        return <div className="stats-tab__error">Failed to load statistics: {error}</div>;
    }

    const { fireProfile, regionContext, tenYearContext } = stats;
    const regionStats = regionContext.regionStats;
    const regionAvg = tenYearContext.regionAverage;

    // --- Chart Data ---

    // Size growth: Discovery → Current (→ Final if out)
    const sizeGrowthData: StatsChartData[] = [
        { name: 'Discovery', value: fireProfile.discovery_size, color: '#66BB6A' },
        { name: 'Current', value: fireProfile.estimated_size, color: '#F57C00' },
    ];
    if (fireProfile.out_size !== null) {
        sizeGrowthData.push({ name: 'Final', value: fireProfile.out_size, color: '#C62828' });
    }

    // Regional response type distribution
    const responseDistData: StatsChartData[] = regionStats ? [
        { name: 'Full Response', value: regionStats.full_response, color: '#C62828' },
        { name: 'Modified', value: regionStats.modified_response, color: '#F57C00' },
        { name: 'Monitor', value: regionStats.monitor_response, color: '#1976D2' },
    ].filter(d => d.value > 0) : [];

    // Region fire count by year (sparkline bar chart)
    const regionYearData: StatsChartData[] = regionContext.regionByYear.map(ry => ({
        name: String(ry.year),
        value: ry.total_fires,
        color: ry.year === fireProfile.year ? '#F57C00' : '#1976D2',
    }));

    // Cause breakdown for region this year
    const causeData: StatsChartData[] = regionStats ? [
        { name: 'Natural', value: regionStats.natural_causes, color: '#2E7D32' },
        { name: 'Human', value: regionStats.human_causes, color: '#C62828' },
    ].filter(d => d.value > 0) : [];

    // Status breakdown for region this year
    const statusData: StatsChartData[] = regionStats ? [
        { name: 'Out of Control', value: regionStats.out_of_control, color: '#C62828' },
        { name: 'Being Held', value: regionStats.being_held, color: '#F57C00' },
        { name: 'Under Control', value: regionStats.under_control, color: '#1976D2' },
        { name: 'Declared Out', value: regionStats.declared_out, color: '#2E7D32' },
        { name: 'New', value: regionStats.new_fires, color: '#7B1FA2' },
    ].filter(d => d.value > 0) : [];

    return (
        <div className="wildfire-detail-content stats-tab">

            {/* --- Section 1: Fire Profile Card --- */}
            <div className="detail-section">
                <h3>Fire Profile</h3>
                <div className="stats-tab__profile-card">
                    <div className="stats-tab__profile-grid">
                        <div className="stats-tab__profile-item">
                            <label>Status</label>
                            <span className={`stats-tab__status stats-tab__status--${fireProfile.status.toLowerCase()}`}>
                                {formatStatus(fireProfile.status)}
                            </span>
                        </div>
                        <div className="stats-tab__profile-item">
                            <label>Size</label>
                            <span className="stats-tab__big-number">{formatHa(fireProfile.estimated_size)}</span>
                        </div>
                        <div className="stats-tab__profile-item">
                            <label>Days Active</label>
                            <span className="stats-tab__big-number">{fireProfile.days_active ?? '—'}</span>
                        </div>
                        <div className="stats-tab__profile-item">
                            <label>Response</label>
                            <span>{fireProfile.response_category}</span>
                        </div>
                        <div className="stats-tab__profile-item">
                            <label>Cause</label>
                            <span>{fireProfile.cause || '—'}{fireProfile.sub_cause ? ` / ${fireProfile.sub_cause}` : ''}</span>
                        </div>
                        <div className="stats-tab__profile-item">
                            <label>Fire Level</label>
                            <span>{fireProfile.fire_level || '—'}</span>
                        </div>
                        {fireProfile.initial_attack_success !== null && (
                            <div className="stats-tab__profile-item">
                                <label>Initial Attack</label>
                                <span className={fireProfile.initial_attack_success ? 'stats-tab__ia-success' : 'stats-tab__ia-fail'}>
                                    {fireProfile.initial_attack_success ? 'Success' : 'Escaped'}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Size growth bar */}
                <div className="stats-tab__chart-row">
                    <StatsBarChart data={sizeGrowthData} title="Size Progression (ha)" color="#F57C00" />
                </div>
            </div>

            {/* --- Section 2: This Fire vs. Region --- */}
            {regionStats && fireProfile.region_name && (
                <div className="detail-section">
                    <h3>This Fire vs. {fireProfile.region_name} Region ({fireProfile.year})</h3>

                    {/* Percentile callout */}
                    {regionContext.sizePercentile !== null && (
                        <div className="stats-tab__callout">
                            <span className="stats-tab__callout-number">{regionContext.sizePercentile}th</span>
                            <span className="stats-tab__callout-text">
                                percentile — this fire is <strong>{percentileLabel(regionContext.sizePercentile)}</strong> for {fireProfile.region_name} in {fireProfile.year}.
                                {' '}Region has {regionStats.total_fires} fires totalling {formatHa(regionStats.total_area)}.
                            </span>
                        </div>
                    )}

                    {/* Duration comparison */}
                    {fireProfile.days_active !== null && regionContext.regionAvgDuration !== null && (
                        <div className="stats-tab__comparison-row">
                            <div className="stats-tab__comparison-item">
                                <label>This Fire</label>
                                <span className="stats-tab__big-number">{fireProfile.days_active} days</span>
                            </div>
                            <div className="stats-tab__comparison-divider">vs</div>
                            <div className="stats-tab__comparison-item">
                                <label>Region Average</label>
                                <span className="stats-tab__big-number">{regionContext.regionAvgDuration} days</span>
                            </div>
                        </div>
                    )}

                    <div className="stats-tab__chart-grid">
                        <StatsPieChart data={responseDistData} title="Region Response Distribution" />
                        <StatsPieChart data={causeData} title="Region Causes" />
                        <StatsPieChart data={statusData} title="Region Status Breakdown" />
                    </div>
                </div>
            )}

            {/* --- Section 3: 10-Year Context --- */}
            {regionAvg && fireProfile.region_name && (
                <div className="detail-section">
                    <h3>{fireProfile.region_name} — 10-Year Context</h3>

                    <div className="stats-tab__context-cards">
                        <div className="stats-tab__context-card">
                            <label>10-Year Avg Fires/Year</label>
                            <span className="stats-tab__big-number">{regionAvg.total_fires}</span>
                            {regionStats && (
                                <span className={`stats-tab__delta ${regionStats.total_fires > regionAvg.total_fires ? 'stats-tab__delta--above' : 'stats-tab__delta--below'}`}>
                                    {fireProfile.year}: {regionStats.total_fires} ({regionStats.total_fires > regionAvg.total_fires ? '+' : ''}{regionStats.total_fires - regionAvg.total_fires})
                                </span>
                            )}
                        </div>
                        <div className="stats-tab__context-card">
                            <label>10-Year Avg Area/Year</label>
                            <span className="stats-tab__big-number">{formatHa(regionAvg.total_area)}</span>
                            {regionStats && (
                                <span className={`stats-tab__delta ${regionStats.total_area > regionAvg.total_area ? 'stats-tab__delta--above' : 'stats-tab__delta--below'}`}>
                                    {fireProfile.year}: {formatHa(regionStats.total_area)}
                                </span>
                            )}
                        </div>
                        <div className="stats-tab__context-card">
                            <label>10-Year Avg Fire Size</label>
                            <span className="stats-tab__big-number">{formatHa(regionAvg.avg_size)}</span>
                            <span className={`stats-tab__delta ${fireProfile.estimated_size > regionAvg.avg_size ? 'stats-tab__delta--above' : 'stats-tab__delta--below'}`}>
                                This fire: {formatHa(fireProfile.estimated_size)}
                            </span>
                        </div>
                        <div className="stats-tab__context-card">
                            <label>10-Year Avg IA Success Rate</label>
                            <span className="stats-tab__big-number">
                                {regionAvg.ia_success + regionAvg.ia_fail > 0
                                    ? `${Math.round((regionAvg.ia_success / (regionAvg.ia_success + regionAvg.ia_fail)) * 100)}%`
                                    : '—'}
                            </span>
                        </div>
                    </div>

                    <div className="stats-tab__chart-row">
                        <StatsBarChart data={regionYearData} title={`${fireProfile.region_name} — Fires by Year`} color="#1976D2" />
                    </div>
                </div>
            )}
        </div>
    );
};
